import React from 'react'
import { interpolate } from '../utils/proposalVariables'
import { calculateMonthlyPrice, isAsNeeded, isIncludedWithPlan, getFrequencyDisplayLabel } from '../utils/frequencyConfig'

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0)
}

// Backward-compat: use perVisitPrice if present, fall back to monthlyPrice
const getMonthlyPrice = (svc) => {
  if (svc.perVisitPrice != null && svc.perVisitPrice !== '') {
    return calculateMonthlyPrice(svc.perVisitPrice, svc.frequency)
  }
  return Number(svc.monthlyPrice) || 0
}

// Fallback image map for services that don't have an explicit imageUrl
const DEFAULT_IMAGE_MAP = {
  'basic maintenance': '/build/images/proposals/edging.webp',
  irrigation: '/build/images/proposals/sprinklers.webp',
  trimming: '/build/images/proposals/trimming_close.webp',
  'mowing (if applicable)': '/build/images/proposals/edging.webp',
  mowing: '/build/images/proposals/edging.webp',
  'weed control & pre-emergent': '/build/images/proposals/spray_close.webp',
  'weed control': '/build/images/proposals/spray_close.webp',
  'plant replacement': '/build/images/proposals/tree_trim_close.webp',
  'pest control': '/build/images/proposals/pest_control.webp',
}

const getServiceImage = (svc) => {
  if (svc.imageUrl) return svc.imageUrl
  return DEFAULT_IMAGE_MAP[svc.name?.toLowerCase()] || ''
}

const ScopeOfServices = ({ data, variables, renderMode }) => {
  const allServices = data?.services || []
  const visibleServices = allServices.filter(svc => !svc.hidden)
  const showTotal = data?.showAnnualTotal !== false

  // Exclude as-needed services from total
  const monthlyTotal = visibleServices.reduce((sum, svc) => {
    if (isAsNeeded(svc.frequency)) return sum
    return sum + getMonthlyPrice(svc)
  }, 0)

  // When renderMode is 'table-only', skip individual service detail pages
  // (they're rendered separately as fixed pages by ProposalPreview)
  const showDetailPages = renderMode !== 'table-only'

  return (
    <>
      {/* Summary Table */}
      <div className="proposal-section">
        <div className="proposal-section__headline">{data?.headline || 'Scope of Services'}</div>
        <div className="proposal-section__accent-line" />
        <table className="proposal-scope__table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Frequency</th>
              <th>Monthly Price</th>
            </tr>
          </thead>
          <tbody>
            {visibleServices.map((svc, idx) => {
              const asNeeded = isAsNeeded(svc.frequency)
              const included = isIncludedWithPlan(svc.frequency)

              return (
                <tr key={idx}>
                  <td>
                    <div className="proposal-scope__service-name">{svc.name}</div>
                    {svc.shortDescription && (
                      <div className="proposal-scope__service-desc">{interpolate(svc.shortDescription, variables)}</div>
                    )}
                  </td>
                  <td>
                    {getFrequencyDisplayLabel(svc.frequency)}
                    {included === true && (
                      <div className="proposal-scope__inclusion-badge proposal-scope__inclusion-badge--included">
                        Included w/ Mtc Plan
                      </div>
                    )}
                    {included === false && (
                      <div className="proposal-scope__inclusion-badge proposal-scope__inclusion-badge--additional">
                        Additional Costs May Apply
                      </div>
                    )}
                  </td>
                  <td>{asNeeded ? '\u2014' : formatCurrency(getMonthlyPrice(svc))}</td>
                </tr>
              )
            })}
          </tbody>
          {showTotal && (
            <tfoot>
              <tr className="proposal-scope__total-row">
                <td colSpan={2}>{data?.annualTotalLabel || 'Total Monthly Maintenance Fee'}</td>
                <td>{formatCurrency(monthlyTotal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {data?.notes && (
          <div className="proposal-scope__notes">{interpolate(data.notes, variables)}</div>
        )}
      </div>

      {/* Service Detail Pages (only when NOT in table-only mode) */}
      {showDetailPages && visibleServices.filter(svc => svc.description).map((svc, idx) => {
        const img = getServiceImage(svc)
        return (
          <div
            className={`proposal-service-detail${!img ? ' proposal-service-detail--no-image' : ''}${idx % 2 === 1 ? ' proposal-service-detail--alt' : ''}`}
            key={idx}
          >
            {img && (
              <div className="proposal-service-detail__image">
                <img src={img} alt={svc.name} />
              </div>
            )}
            <div className="proposal-service-detail__content">
              <div className="proposal-service-detail__title">{svc.name}</div>
              <div className="proposal-service-detail__accent-line" />
              <div className="proposal-service-detail__body">{interpolate(svc.description, variables)}</div>
            </div>
          </div>
        )
      })}
    </>
  )
}

export default ScopeOfServices
